import React, { useRef, useState, useEffect, useMemo } from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  Collapse,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Description as DocIcon,
  CheckCircle as CheckIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  PlayArrow as RunIcon,
  Refresh as RetryIcon,
  Cancel as FailIcon,
  ThumbUp as ConfirmIcon,
  SwapHoriz as OverrideIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { tokens } from "@/tokens";
import {
  useUploadFirstSaleDocumentMutation,
  useDeleteFirstSaleDocumentMutation,
  useGetFirstSaleDocumentSectionsQuery,
  useLazyGetFirstSaleDocumentUrlQuery,
  useRunFirstSaleValidationMutation,
  useRerunFirstSaleValidationCheckMutation,
  useUpdateValidationCheckReviewMutation,
  useSignOffValidationMutation,
  useGetFirstSaleValidationQuery,
} from "@/services/first-sale";
import { DocumentViewerModal } from "./document-viewer-modal";
import { ChecklistItemDrawer } from "./checklist-item-drawer";
import { ValidationDetailDrawer } from "./validation-detail-drawer";
import { DocumentRequestBar, type DocumentRequestItem } from "./document-request-bar";
import {
  useCreateFirstSaleDocumentRequestsMutation,
  useGetFirstSaleDocumentRequestsQuery,
  useFulfillFirstSaleDocumentRequestMutation,
  useUpdateFirstSaleDocumentRequestMutation,
  useDeleteFirstSaleDocumentRequestMutation,
  useConfirmFirstSaleDocumentUploadMutation,
} from "@/services/first-sale/first-sale-api";
import { CHECK_TO_SLOT, type InlineChecklistData, type CheckItemResult } from "./inline-validation-checklist";
import { DocumentSlotRow } from "./document-slot-row";
import { DataExtractionPanel } from "./data-extraction-panel";
import { CustomRequestBlock, RequestRevisionDialog, type ValidationIssue } from "./document-request-section";

const isPreviewable = (mimeType: string) =>
  mimeType === "application/pdf" || mimeType.startsWith("image/");

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

interface DocumentListProps {
  caseId: string;
  supplierId: string;
  documents: any[];
  tenantId?: string;
  validation?: any;
  multipleMiddlemen?: boolean;
  backToBackFOB?: boolean;
  materialsProvided?: boolean;
  relatedParties?: boolean;
  factorySellsToUnrelated?: boolean;
  middlemanBuysFromUnrelated?: boolean;
  middlemanValue?: number;
  dutyRate?: number;
  htsCode?: string;
}

export const DocumentList: React.FC<DocumentListProps> = ({
  caseId,
  supplierId,
  documents,
  tenantId,
  validation,
  multipleMiddlemen,
  backToBackFOB,
  materialsProvided,
  relatedParties,
  factorySellsToUnrelated,
  middlemanBuysFromUnrelated,
  middlemanValue,
  dutyRate,
  htsCode,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const additionalFileInputRef = useRef<HTMLInputElement>(null);
  const [uploadTarget, setUploadTarget] = useState<{ category: string; slotId: string } | null>(null);
  const revisionFileInputRef = useRef<HTMLInputElement>(null);
  const [revisionUploadTarget, setRevisionUploadTarget] = useState<{
    requestId: string;
    category: string;
    slotId: string;
    reason: string;
  } | null>(null);
  const [viewingDoc, setViewingDoc] = useState<any>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [additionalExpanded, setAdditionalExpanded] = useState(true);
  const [additionalModalOpen, setAdditionalModalOpen] = useState(false);
  const [additionalAnnotation, setAdditionalAnnotation] = useState("");
  const [additionalUploading, setAdditionalUploading] = useState(false);

  // ─── Checklist item detail drawer state ─────────────────────────
  const [drawerItem, setDrawerItem] = useState<CheckItemResult | null>(null);
  const [drawerCheckName, setDrawerCheckName] = useState("");
  const [drawerReasoning, setDrawerReasoning] = useState<string | undefined>();
  const [drawerCitations, setDrawerCitations] = useState<any[] | undefined>();

  const handleChecklistItemClick = (
    item: CheckItemResult,
    checkName: string,
    reasoning?: string,
    citations?: any[],
  ) => {
    setDrawerItem(item);
    setDrawerCheckName(checkName);
    setDrawerReasoning(reasoning);
    setDrawerCitations(citations);
  };

  // ─── Cross-doc check state ───────────────────────────────────────
  const [expandedCrossDoc, setExpandedCrossDoc] = useState<Set<string>>(new Set());
  const [detailCheck, setDetailCheck] = useState<any>(null);

  const toggleCrossDocExpand = (checkId: string) => {
    setExpandedCrossDoc((prev) => {
      const next = new Set(prev);
      if (next.has(checkId)) next.delete(checkId);
      else next.add(checkId);
      return next;
    });
  };

  // ─── Document request accumulator ─────────────────────────────────
  const [requestTarget, setRequestTarget] = useState<{ slotId: string; label: string } | null>(null);
  const [requestReason, setRequestReason] = useState("");
  const [requestReqs, setRequestReqs] = useState("");
  const [pendingRequests, setPendingRequests] = useState<DocumentRequestItem[]>([]);
  const [createRequests, { isLoading: sendingRequests }] = useCreateFirstSaleDocumentRequestsMutation();
  const [updateRequest] = useUpdateFirstSaleDocumentRequestMutation();
  const [deleteRequest] = useDeleteFirstSaleDocumentRequestMutation();
  const [editingRequest, setEditingRequest] = useState<any>(null);
  const [editReason, setEditReason] = useState("");
  const [editReqs, setEditReqs] = useState("");
  const { data: existingRequests = [] } = useGetFirstSaleDocumentRequestsQuery(
    { caseId, ...(tenantId && { tenantId }) },
  );
  const existingRequestSlotIds = useMemo(
    () => new Set(existingRequests.filter((r: any) => r.status === "pending" && r.slotId).map((r: any) => r.slotId)),
    [existingRequests]
  );
  const pendingSlotIds = useMemo(
    () => new Set(pendingRequests.filter((r): r is DocumentRequestItem & { slotId: string } => !!r.slotId).map((r) => r.slotId)),
    [pendingRequests]
  );

  const addDocumentRequest = (slotId: string | undefined, label: string, reason: string, requirements?: string[], linkedIssues?: ValidationIssue[]) => {
    setPendingRequests((prev) => [
      ...prev,
      {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        slotId,
        label,
        reason,
        requirements: requirements ?? [],
        linkedIssues,
      },
    ]);
  };

  const handleSendRequests = async (notifyEmail?: string) => {
    await createRequests({
      caseId,
      ...(tenantId && { tenantId }),
      requests: pendingRequests.map(({ slotId, label, description, reason, requirements, linkedIssues }) => ({
        slotId,
        label,
        description,
        reason,
        requirements,
        linkedIssues: linkedIssues?.map(({ itemId, status, detail }) => ({ itemId, status, detail })),
      })),
      notifyEmail,
    }).unwrap();
    setPendingRequests([]);
  };

  const handleEditRequest = (req: any) => {
    setEditingRequest(req);
    setEditReason(req.reason);
    setEditReqs((req.requirements || []).join("\n"));
  };

  const handleDeleteRequest = async (requestId: string) => {
    await deleteRequest({ requestId }).unwrap();
  };

  // ─── Validation controls ─────────────────────────────────────────
  const tVars = tenantId ? { tenantId } : {};
  const [polling, setPolling] = useState(false);
  const [signOffNotes, setSignOffNotes] = useState("");

  const { data: liveValidation } = useGetFirstSaleValidationQuery(
    { caseId, ...tVars },
    { pollingInterval: polling ? 2000 : 0 }
  );
  // Use live validation when polling, otherwise use passed-in validation
  const activeValidation = liveValidation ?? validation;
  const isProcessing = activeValidation?.processingStatus === "PROCESSING";

  useEffect(() => {
    if (isProcessing) setPolling(true);
    else if (polling) setPolling(false);
  }, [isProcessing, polling]);

  const [runValidation, { isLoading: runningValidation }] = useRunFirstSaleValidationMutation();
  const [rerunCheck] = useRerunFirstSaleValidationCheckMutation();
  const [rerunningCheckId, setRerunningCheckId] = useState<string | null>(null);
  const [updateReview] = useUpdateValidationCheckReviewMutation();
  const [signOff, { isLoading: signingOff }] = useSignOffValidationMutation();

  const handleRerun = async (checkId: string) => {
    setRerunningCheckId(checkId);
    try {
      await rerunCheck({ caseId, checkId, ...tVars }).unwrap();
    } catch (err) {
      console.error("Rerun failed:", err);
    } finally {
      setRerunningCheckId(null);
    }
  };

  const handleRunValidation = async () => {
    try {
      await runValidation({ caseId, ...tVars }).unwrap();
      setPolling(true);
    } catch (err) {
      console.error("Validation failed:", err);
    }
  };

  const handleReviewUpdate = async (checkId: string, reviewStatus: string, reviewNote?: string) => {
    const input: Record<string, string> = { reviewStatus };
    if (reviewNote !== undefined) input.reviewNote = reviewNote;
    await updateReview({ caseId, checkId, ...tVars, input }).unwrap();
  };

  const handleSignOff = async (approved: boolean) => {
    await signOff({ caseId, ...tVars, approved, notes: signOffNotes || undefined }).unwrap();
  };

  const { data: sections = [] } = useGetFirstSaleDocumentSectionsQuery({
    multipleMiddlemen,
    backToBackFOB,
    materialsProvided,
    relatedParties,
    factorySellsToUnrelated,
    middlemanBuysFromUnrelated,
  });
  const [uploadDocument, { isLoading: uploading }] = useUploadFirstSaleDocumentMutation();
  const [deleteDocument] = useDeleteFirstSaleDocumentMutation();
  const [confirmUpload] = useConfirmFirstSaleDocumentUploadMutation();
  const [fetchDocUrl] = useLazyGetFirstSaleDocumentUrlQuery();

  // Start with all sections expanded
  React.useEffect(() => {
    if (sections.length > 0 && expandedSections.size === 0) {
      setExpandedSections(new Set(sections.map((s: any) => s.sectionId)));
    }
  }, [sections]);

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  };

  const handleUploadClick = (category: string, slotId: string) => {
    setUploadTarget({ category, slotId });
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !uploadTarget) return;

    try {
      const result = await uploadDocument({
        input: {
          ...(tenantId && { tenantId }),
          caseId,
          supplierId,
          category: uploadTarget.category.toUpperCase(),
          slotId: uploadTarget.slotId,
          filename: file.name,
          mimeType: file.type,
          sizeBytes: file.size,
        },
      }).unwrap();

      // Upload to S3 using the signed URL
      await fetch(result.uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });

      // Trigger language detection in the background
      confirmUpload({ docId: result.docId, ...(tenantId && { tenantId }) });
    } catch (err) {
      console.error("Upload failed:", err);
    }

    // Reset
    setUploadTarget(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const [fulfillRequest] = useFulfillFirstSaleDocumentRequestMutation();

  const handleRevisionUploadClick = (requestId: string, category: string, slotId: string, reason: string) => {
    setRevisionUploadTarget({ requestId, category, slotId, reason });
    revisionFileInputRef.current?.click();
  };

  const handleRevisionFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !revisionUploadTarget) return;

    try {
      const result = await uploadDocument({
        input: {
          ...(tenantId && { tenantId }),
          caseId,
          supplierId,
          category: revisionUploadTarget.category.toUpperCase(),
          slotId: revisionUploadTarget.slotId,
          filename: file.name,
          mimeType: file.type,
          sizeBytes: file.size,
          annotation: `[REVISION] ${revisionUploadTarget.reason}`,
          revisionRequestId: revisionUploadTarget.requestId,
          revisionReason: revisionUploadTarget.reason,
        },
      }).unwrap();

      await fetch(result.uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });

      // Trigger language detection in the background
      confirmUpload({ docId: result.docId, ...(tenantId && { tenantId }) });

      // Mark the request as fulfilled
      await fulfillRequest({ requestId: revisionUploadTarget.requestId }).unwrap();
    } catch (err) {
      console.error("Revision upload failed:", err);
    }

    setRevisionUploadTarget(null);
    if (revisionFileInputRef.current) revisionFileInputRef.current.value = "";
  };

  const handleDelete = async (docId: string) => {
    try {
      await deleteDocument({ docId, ...(tenantId && { tenantId }) }).unwrap();
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const handleDownload = async (doc: any) => {
    try {
      const result = await fetchDocUrl({ docId: doc._id, ...(tenantId && { tenantId }) }).unwrap();
      if (result?.downloadUrl) {
        const a = window.document.createElement("a");
        a.href = result.downloadUrl;
        a.download = doc.filename || "document";
        window.document.body.appendChild(a);
        a.click();
        window.document.body.removeChild(a);
      }
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  const handleAdditionalFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setAdditionalUploading(true);
    try {
      const result = await uploadDocument({
        input: {
          ...(tenantId && { tenantId }),
          caseId,
          supplierId,
          category: "ADDITIONAL",
          slotId: "additional",
          filename: file.name,
          mimeType: file.type,
          sizeBytes: file.size,
          ...(additionalAnnotation.trim() && { annotation: additionalAnnotation.trim() }),
        },
      }).unwrap();

      await fetch(result.uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });

      // Trigger language detection in the background
      confirmUpload({ docId: result.docId, ...(tenantId && { tenantId }) });

      setAdditionalAnnotation("");
      setAdditionalModalOpen(false);
    } catch (err) {
      console.error("Upload failed:", err);
    } finally {
      setAdditionalUploading(false);
    }

    if (additionalFileInputRef.current) additionalFileInputRef.current.value = "";
  };

  const additionalDocs = documents.filter((doc: any) => doc.category === "ADDITIONAL");

  const getDocsForSlot = (slotId: string) =>
    documents.filter((doc: any) => doc.slotId === slotId);

  const getSectionDocCount = (section: any) =>
    section.slots.reduce((sum: number, slot: any) => sum + getDocsForSlot(slot.slotId).length, 0);

  const getSectionRequiredCount = (section: any) => {
    const required = section.slots.filter((s: any) => s.required);
    const filled = required.filter((s: any) => getDocsForSlot(s.slotId).length > 0);
    return { filled: filled.length, total: required.length };
  };

  // Build lookup: slotId → checklist items from validation data
  const checklistBySlot: Record<string, InlineChecklistData> = {};
  if (activeValidation?.requirements) {
    for (const req of activeValidation.requirements) {
      if (!req.items || req.items.length === 0) continue;
      const slotId = CHECK_TO_SLOT[req.checkId] || req.checkId;
      checklistBySlot[slotId] = {
        checkId: req.checkId,
        checkName: req.checkName,
        status: req.status,
        detail: req.detail || "",
        items: req.items,
        reasoning: req.reasoning,
        citations: req.citations,
      };
    }
  }

  const crossDocChecks = activeValidation?.crossDocValidation ?? [];
  const overallScore = activeValidation?.overallScore;
  const completedChecks = activeValidation?.completedChecks ?? 0;
  const totalChecks = activeValidation?.totalChecks ?? 0;

  return (
    <Box>
      {/* ─── Validation Controls Bar ─────────────────────────────── */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 2,
          py: 1.5,
          mb: 1.5,
          bgcolor: tokens.bg.surface,
          border: `1px solid ${tokens.border.default}`,
          borderRadius: tokens.radius.md,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          {overallScore != null && (
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  border: `3px solid ${overallScore >= 80 ? tokens.status.success : overallScore >= 60 ? tokens.status.warning : tokens.status.danger}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography sx={{ fontSize: 10, fontWeight: tokens.fontWeight.heavy, color: tokens.text.primary }}>
                  {overallScore}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.secondary }}>
                Validation Score
              </Typography>
            </Stack>
          )}
          {isProcessing && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 120 }}>
              <LinearProgress
                variant="determinate"
                value={totalChecks > 0 ? (completedChecks / totalChecks) * 100 : 0}
                sx={{ flex: 1, height: 6, borderRadius: 3 }}
              />
              <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, whiteSpace: "nowrap" }}>
                {completedChecks}/{totalChecks}
              </Typography>
            </Stack>
          )}
        </Stack>
        <Button
          size="small"
          variant="contained"
          startIcon={isProcessing || runningValidation ? <CircularProgress size={14} color="inherit" /> : <RunIcon sx={{ fontSize: 16 }} />}
          onClick={handleRunValidation}
          disabled={isProcessing || runningValidation}
          sx={{
            textTransform: "none",
            fontSize: tokens.fontSize.xs,
            fontWeight: tokens.fontWeight.semibold,
            bgcolor: tokens.brand.primary,
            borderRadius: tokens.radius.sm,
            boxShadow: "none",
            px: 2,
            "&:hover": { bgcolor: tokens.brand.primaryHover, boxShadow: "none" },
          }}
        >
          {isProcessing ? "Validating..." : runningValidation ? "Starting..." : "Run AI Validation"}
        </Button>
      </Stack>

      <input
        ref={fileInputRef}
        type="file"
        hidden
        onChange={handleFileChange}
        accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx"
      />
      <input
        ref={revisionFileInputRef}
        type="file"
        hidden
        onChange={handleRevisionFileChange}
        accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx"
      />

      {sections.filter((s: any) => s.sectionId !== "additional").map((section: any) => {
        const isExpanded = expandedSections.has(section.sectionId);
        const docCount = getSectionDocCount(section);
        const required = getSectionRequiredCount(section);

        return (
          <Box
            key={section.sectionId}
            sx={{
              mb: 2,
              border: `1px solid ${tokens.border.container}`,
              borderRadius: tokens.radius.md,
              overflow: "hidden",
            }}
          >
            {/* Section Header */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              onClick={() => toggleSection(section.sectionId)}
              sx={{
                px: 2,
                py: 1.5,
                bgcolor: tokens.bg.surface,
                borderBottom: isExpanded ? `1px solid ${tokens.border.light}` : "none",
                cursor: "pointer",
                "&:hover": { bgcolor: tokens.bg.hover },
                transition: "background-color 150ms ease",
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Typography
                  sx={{
                    fontSize: tokens.fontSize.sm2,
                    fontWeight: tokens.fontWeight.heavy,
                    color: tokens.text.primary,
                  }}
                >
                  {section.label}
                </Typography>
                <Chip
                  label={`${docCount} file${docCount !== 1 ? "s" : ""}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 20,
                    fontSize: tokens.fontSize["2xs"],
                    borderColor: docCount > 0 ? `${tokens.status.success}40` : tokens.border.light,
                    color: docCount > 0 ? tokens.status.success : tokens.text.muted,
                  }}
                />
                {required.total > 0 && (
                  <Chip
                    label={`${required.filled}/${required.total} required`}
                    size="small"
                    variant="outlined"
                    sx={{
                      height: 20,
                      fontSize: tokens.fontSize["2xs"],
                      borderColor: required.filled === required.total
                        ? `${tokens.status.success}40`
                        : `${tokens.status.warning}40`,
                      color: required.filled === required.total
                        ? tokens.status.success
                        : tokens.status.warning,
                    }}
                  />
                )}
              </Stack>
              {isExpanded ? (
                <CollapseIcon sx={{ fontSize: 18, color: tokens.text.muted }} />
              ) : (
                <ExpandIcon sx={{ fontSize: 18, color: tokens.text.muted }} />
              )}
            </Stack>

            {/* Section Content */}
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Stack spacing={0} sx={{ px: 0 }}>
                {section.slots.map((slot: any, slotIdx: number) => (
                  <DocumentSlotRow
                    key={slot.slotId}
                    slot={slot}
                    sectionId={section.sectionId}
                    slotDocs={getDocsForSlot(slot.slotId)}
                    isLastSlot={slotIdx === section.slots.length - 1}
                    uploading={uploading}
                    uploadTargetSlotId={uploadTarget?.slotId}
                    checklistData={checklistBySlot[slot.slotId]}
                    rerunningCheckId={rerunningCheckId}
                    existingRequestSlotIds={existingRequestSlotIds}
                    pendingSlotIds={pendingSlotIds}
                    existingRequests={existingRequests}
                    onUploadClick={handleUploadClick}
                    onViewDoc={setViewingDoc}
                    onDownloadDoc={handleDownload}
                    onDeleteDoc={handleDelete}
                    onChecklistItemClick={handleChecklistItemClick}
                    onRerun={handleRerun}
                    onRequestRevision={(slotId, label) => setRequestTarget({ slotId, label })}
                    onRevisionUploadClick={handleRevisionUploadClick}
                    onEditRequest={handleEditRequest}
                    onDeleteRequest={handleDeleteRequest}
                  />
                ))}

                {/* Custom requests (no slotId) shown in the last section */}
                <CustomRequestBlock
                  existingRequests={existingRequests}
                  sectionId={section.sectionId}
                  onRevisionUploadClick={handleRevisionUploadClick}
                  onEditRequest={handleEditRequest}
                  onDeleteRequest={handleDeleteRequest}
                />
              </Stack>
            </Collapse>
          </Box>
        );
      })}

      {/* Additional Documents Section */}
      <Box
        sx={{
          mb: 2,
          border: `1px solid ${tokens.border.container}`,
          borderRadius: tokens.radius.md,
          overflow: "hidden",
        }}
      >
        <input
          ref={additionalFileInputRef}
          type="file"
          hidden
          onChange={handleAdditionalFileChange}
          accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx"
        />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2,
            py: 1.5,
            bgcolor: tokens.bg.surface,
            borderBottom: additionalExpanded && additionalDocs.length > 0 ? `1px solid ${tokens.border.light}` : "none",
            cursor: "pointer",
            "&:hover": { bgcolor: tokens.bg.hover },
            transition: "background-color 150ms ease",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            onClick={() => setAdditionalExpanded(!additionalExpanded)}
            sx={{ flex: 1, cursor: "pointer" }}
          >
            <Typography
              sx={{
                fontSize: tokens.fontSize.sm2,
                fontWeight: tokens.fontWeight.heavy,
                color: tokens.text.primary,
              }}
            >
              Additional Documents
            </Typography>
            <Chip
              label={`${additionalDocs.length} file${additionalDocs.length !== 1 ? "s" : ""}`}
              size="small"
              variant="outlined"
              sx={{
                height: 20,
                fontSize: tokens.fontSize["2xs"],
                borderColor: additionalDocs.length > 0 ? `${tokens.status.success}40` : tokens.border.light,
                color: additionalDocs.length > 0 ? tokens.status.success : tokens.text.muted,
              }}
            />
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<UploadIcon sx={{ fontSize: 16 }} />}
              onClick={(e) => { e.stopPropagation(); setAdditionalModalOpen(true); }}
              sx={{
                textTransform: "none",
                fontSize: tokens.fontSize.xs,
                fontWeight: tokens.fontWeight.semibold,
                color: tokens.text.primary,
                borderColor: tokens.border.default,
                borderRadius: tokens.radius.sm,
                px: 2,
                py: 0.5,
                minWidth: 90,
                flexShrink: 0,
                "&:hover": { bgcolor: tokens.bg.hover, borderColor: tokens.text.muted },
              }}
            >
              Upload
            </Button>
            {additionalDocs.length > 0 && (
              <Box onClick={() => setAdditionalExpanded(!additionalExpanded)} sx={{ display: "flex" }}>
                {additionalExpanded ? (
                  <CollapseIcon sx={{ fontSize: 18, color: tokens.text.muted }} />
                ) : (
                  <ExpandIcon sx={{ fontSize: 18, color: tokens.text.muted }} />
                )}
              </Box>
            )}
          </Stack>
        </Stack>

        {additionalDocs.length > 0 && (
          <Collapse in={additionalExpanded} timeout="auto" unmountOnExit>
            <Stack spacing={0} sx={{ px: 0 }}>
              {additionalDocs.map((doc: any, idx: number) => (
                <Box
                  key={doc._id}
                  sx={{
                    borderBottom: idx < additionalDocs.length - 1 ? `1px solid ${tokens.border.light}` : "none",
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ px: 2, py: 1.25 }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
                      <CheckIcon sx={{ color: tokens.status.success, fontSize: 18, flexShrink: 0 }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography
                            sx={{
                              fontSize: tokens.fontSize.sm2,
                              fontWeight: tokens.fontWeight.semibold,
                              color: tokens.text.primary,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {doc.filename}
                          </Typography>
                          {doc.sizeBytes && (
                            <Typography
                              sx={{
                                fontSize: tokens.fontSize["2xs"],
                                color: tokens.text.muted,
                                flexShrink: 0,
                              }}
                            >
                              {formatFileSize(doc.sizeBytes)}
                            </Typography>
                          )}
                        </Stack>
                        {doc.annotation && (
                          <Typography
                            sx={{
                              fontSize: tokens.fontSize["2xs"],
                              color: tokens.text.muted,
                              mt: 0.25,
                              fontStyle: "italic",
                            }}
                          >
                            {doc.annotation}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                      {isPreviewable(doc.mimeType) ? (
                        <Tooltip title="View Document">
                          <IconButton
                            size="small"
                            onClick={() => setViewingDoc(doc)}
                            sx={{
                              color: tokens.text.body,
                              "&:hover": { color: tokens.brand.primary, bgcolor: `${tokens.brand.primary}08` },
                            }}
                          >
                            <ViewIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Download">
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(doc)}
                            sx={{
                              color: tokens.text.body,
                              "&:hover": { color: tokens.brand.primary, bgcolor: `${tokens.brand.primary}08` },
                            }}
                          >
                            <DownloadIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(doc._id)}
                          sx={{
                            color: tokens.text.muted,
                            "&:hover": { color: tokens.status.danger, bgcolor: `${tokens.status.danger}08` },
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Collapse>
        )}
      </Box>

      {/* Additional Document Upload Modal */}
      <Dialog
        open={additionalModalOpen}
        onClose={() => { if (!additionalUploading) { setAdditionalModalOpen(false); setAdditionalAnnotation(""); } }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: tokens.radius.md,
          },
        }}
      >
        <DialogTitle sx={{ fontSize: tokens.fontSize.md, fontWeight: tokens.fontWeight.heavy }}>
          Upload Additional Document
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted, mb: 2 }}>
            Add any supplementary document to provide additional context for validation checks.
          </Typography>
          <TextField
            size="small"
            label="Context / Annotation"
            placeholder="e.g., 'Email confirming pricing terms between buyer and seller'"
            value={additionalAnnotation}
            onChange={(e) => setAdditionalAnnotation(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            helperText="Optional. Describe what this document is and how it relates to the case."
            sx={{
              "& .MuiInputBase-root": { fontSize: tokens.fontSize.xs },
              "& .MuiFormHelperText-root": { fontSize: tokens.fontSize["2xs"] },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => { setAdditionalModalOpen(false); setAdditionalAnnotation(""); }}
            disabled={additionalUploading}
            sx={{
              textTransform: "none",
              fontSize: tokens.fontSize.xs,
              color: tokens.text.muted,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={
              additionalUploading
                ? <CircularProgress size={14} sx={{ color: "inherit" }} />
                : <UploadIcon sx={{ fontSize: 16 }} />
            }
            onClick={() => additionalFileInputRef.current?.click()}
            disabled={additionalUploading}
            sx={{
              textTransform: "none",
              fontSize: tokens.fontSize.xs,
              fontWeight: tokens.fontWeight.semibold,
              borderRadius: tokens.radius.sm,
              px: 2.5,
            }}
          >
            {additionalUploading ? "Uploading..." : "Choose File & Upload"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Cross-Document Validation ──────────────────────── */}
      {crossDocChecks.length > 0 && (
        <Box
          sx={{
            mt: 2,
            border: `1px solid ${tokens.border.default}`,
            borderRadius: tokens.radius.md,
            overflow: "hidden",
          }}
        >
          <Box sx={{ px: 2, py: 1.5, bgcolor: "#f8f9fa", borderBottom: `1px solid ${tokens.border.light}` }}>
            <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary }}>
              Cross-Document Validation
            </Typography>
            <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, mt: 0.25 }}>
              Checks consistency across all documents in the transaction chain
            </Typography>
          </Box>
          {crossDocChecks.map((check: any) => {
            const isExpanded = expandedCrossDoc.has(check.checkId);
            const isReviewed = check.reviewStatus === "CONFIRMED" || check.reviewStatus === "OVERRIDDEN";
            const checkProcessing = check.status === "PROCESSING";
            const checkNotRun = check.status === "NOT_RUN";
            const checkDone = !checkProcessing && !checkNotRun;

            const STATUS_ICON: Record<string, React.ReactNode> = {
              PASS: <CheckIcon sx={{ color: "success.main", fontSize: 18 }} />,
              FAIL: <FailIcon sx={{ color: "error.main", fontSize: 18 }} />,
              WARNING: <ExpandIcon sx={{ color: "warning.main", fontSize: 18 }} />,
              NOT_RUN: <DocIcon sx={{ color: "text.disabled", fontSize: 18 }} />,
              PROCESSING: <CircularProgress size={16} thickness={5} />,
            };

            return (
              <Box key={check.checkId} sx={{ borderBottom: `1px solid ${tokens.border.light}`, "&:last-child": { borderBottom: "none" }, opacity: checkNotRun && isProcessing ? 0.5 : 1 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ py: 1, px: 1.5, cursor: checkDone ? "pointer" : "default" }}
                  onClick={() => checkDone && toggleCrossDocExpand(check.checkId)}
                >
                  {STATUS_ICON[check.status] || STATUS_ICON.NOT_RUN}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: 500, color: checkProcessing ? tokens.text.muted : tokens.text.primary }}>
                      {check.checkName}
                      {checkProcessing && <Typography component="span" sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, ml: 1 }}>Analyzing...</Typography>}
                    </Typography>
                    {!isExpanded && checkDone && check.detail && (
                      <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", mt: 0.25 }}>
                        {check.detail}
                      </Typography>
                    )}
                  </Box>
                  {isReviewed && (
                    <Chip label={check.reviewStatus === "CONFIRMED" ? "Confirmed" : "Overridden"} size="small" color={check.reviewStatus === "CONFIRMED" ? "success" : "warning"} variant="outlined" sx={{ height: 20 }} />
                  )}
                  {checkDone && (
                    <Tooltip title="Re-run check">
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); handleRerun(check.checkId); }}
                        disabled={!!rerunningCheckId || isProcessing}
                        sx={{
                          color: tokens.text.muted,
                          "&:hover": { color: tokens.brand.primary },
                          "& svg": rerunningCheckId === check.checkId ? { animation: "spin 1s linear infinite" } : {},
                          "@keyframes spin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } },
                        }}
                      >
                        <RetryIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {checkDone && (isExpanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />)}
                </Stack>

                <Collapse in={isExpanded && checkDone}>
                  <Box sx={{ px: 1.5, pb: 2, pl: 5 }}>
                    {check.detail && (
                      <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.body, mb: 1 }}>{check.detail}</Typography>
                    )}

                    {check.citations?.length > 0 && (
                      <Box mb={1.5}>
                        <Typography sx={{ fontSize: tokens.fontSize["2xs"], fontWeight: 600, color: tokens.text.muted }}>Evidence:</Typography>
                        <Stack spacing={0.5} mt={0.5}>
                          {check.citations.slice(0, 3).map((citation: any, idx: number) => (
                            <Stack key={idx} direction="row" alignItems="center" spacing={1} sx={{ p: 0.5, borderRadius: 0.5, "&:hover": { bgcolor: "#f5f5f5" } }}>
                              <DocIcon sx={{ fontSize: 14, color: tokens.text.muted }} />
                              <Typography sx={{ fontSize: tokens.fontSize["2xs"] }}>
                                {citation.documentName}{citation.pageNumber ? ` (p. ${citation.pageNumber})` : ""}
                              </Typography>
                              {citation.excerpt && (
                                <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, fontStyle: "italic" }}>
                                  &ldquo;{citation.excerpt.slice(0, 80)}{citation.excerpt.length > 80 ? "\u2026" : ""}&rdquo;
                                </Typography>
                              )}
                            </Stack>
                          ))}
                          {check.citations.length > 3 && (
                            <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, ml: 0.5 }}>
                              +{check.citations.length - 3} more
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    )}

                    <Stack direction="row" spacing={1} mt={1} alignItems="center">
                      {(check.reasoning || check.citations?.length > 0) && (
                        <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); setDetailCheck(check); }}
                          sx={{ textTransform: "none", fontSize: 12, fontWeight: tokens.fontWeight.semibold, color: tokens.text.primary, borderColor: tokens.border.default, borderRadius: tokens.radius.sm, "&:hover": { bgcolor: tokens.bg.hover } }}
                        >
                          View Details
                        </Button>
                      )}
                      <Button size="small" variant={check.reviewStatus === "CONFIRMED" ? "contained" : "outlined"} color="success" startIcon={<ConfirmIcon sx={{ fontSize: 14 }} />}
                        onClick={() => handleReviewUpdate(check.checkId, "CONFIRMED")} sx={{ textTransform: "none", fontSize: 12 }}
                      >
                        Confirm
                      </Button>
                      <Button size="small" variant={check.reviewStatus === "OVERRIDDEN" ? "contained" : "outlined"} color="warning" startIcon={<OverrideIcon sx={{ fontSize: 14 }} />}
                        onClick={() => handleReviewUpdate(check.checkId, "OVERRIDDEN")} sx={{ textTransform: "none", fontSize: 12 }}
                      >
                        Override
                      </Button>
                      {isReviewed && (
                        <Button size="small" variant="outlined" startIcon={<ClearIcon sx={{ fontSize: 14 }} />}
                          onClick={() => handleReviewUpdate(check.checkId, "PENDING")} sx={{ textTransform: "none", fontSize: 12 }}
                        >
                          Clear
                        </Button>
                      )}
                    </Stack>

                    {check.reviewNote && (
                      <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, mt: 1 }}>Note: {check.reviewNote}</Typography>
                    )}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      )}

      {/* ─── Data Extraction ────────────────────────────────── */}
      <DataExtractionPanel caseId={caseId} tenantId={tenantId} materialsProvided={materialsProvided} middlemanValue={middlemanValue} dutyRate={dutyRate} htsCode={htsCode} />

      {/* ─── Sign-Off Section ──────────────────────────────── */}
      {activeValidation && !isProcessing && activeValidation.processingStatus === "COMPLETED" && !activeValidation.signedOff && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            border: `1px solid ${tokens.border.default}`,
            borderRadius: tokens.radius.md,
            bgcolor: tokens.bg.surface,
          }}
        >
          <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, mb: 1 }}>
            Case Sign-Off
          </Typography>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            value={signOffNotes}
            onChange={(e) => setSignOffNotes(e.target.value)}
            placeholder="Optional notes for sign-off..."
            sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { fontSize: tokens.fontSize.sm2, borderRadius: tokens.radius.md } }}
          />
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              color="success"
              size="small"
              startIcon={<ConfirmIcon sx={{ fontSize: 14 }} />}
              onClick={() => handleSignOff(true)}
              disabled={signingOff}
              sx={{ textTransform: "none", fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.semibold, boxShadow: "none" }}
            >
              Approve Case
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<FailIcon sx={{ fontSize: 14 }} />}
              onClick={() => handleSignOff(false)}
              disabled={signingOff}
              sx={{ textTransform: "none", fontSize: tokens.fontSize.xs }}
            >
              Reject Case
            </Button>
          </Stack>
        </Box>
      )}

      {activeValidation?.signedOff && (
        <Box sx={{ mt: 2, p: 2, bgcolor: activeValidation.signOffApproved ? `${tokens.status.success}08` : `${tokens.status.danger}08`, border: `1px solid ${activeValidation.signOffApproved ? tokens.status.success : tokens.status.danger}20`, borderRadius: tokens.radius.md }}>
          <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: activeValidation.signOffApproved ? tokens.status.success : tokens.status.danger }}>
            Case {activeValidation.signOffApproved ? "Approved" : "Rejected"}
          </Typography>
          {activeValidation.signOffNotes && (
            <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.secondary, mt: 0.5 }}>
              {activeValidation.signOffNotes}
            </Typography>
          )}
        </Box>
      )}

      {/* Document Viewer Modal */}
      <DocumentViewerModal
        document={viewingDoc}
        open={!!viewingDoc}
        onClose={() => setViewingDoc(null)}
        tenantId={tenantId}
      />

      {/* Checklist Item Detail Drawer */}
      <ChecklistItemDrawer
        open={!!drawerItem}
        onClose={() => setDrawerItem(null)}
        checkName={drawerCheckName}
        item={drawerItem}
        reasoning={drawerReasoning}
        citations={drawerCitations}
      />

      {/* Cross-Doc Validation Detail Drawer */}
      <ValidationDetailDrawer
        check={detailCheck}
        open={!!detailCheck}
        onClose={() => setDetailCheck(null)}
        onCitationClick={() => {}}
        onRerun={(checkId) => handleRerun(checkId)}
        onReviewUpdate={(checkId, status, note) => handleReviewUpdate(checkId, status, note)}
        rerunning={!!rerunningCheckId}
      />

      {/* Request Revision Dialog */}
      <RequestRevisionDialog
        requestTarget={requestTarget}
        requestReason={requestReason}
        requestReqs={requestReqs}
        validationIssues={
          requestTarget?.slotId && checklistBySlot[requestTarget.slotId]
            ? checklistBySlot[requestTarget.slotId].items.filter(
                (i) => i.status === "FAIL" || i.status === "WARNING"
              )
            : undefined
        }
        onReasonChange={setRequestReason}
        onReqsChange={setRequestReqs}
        onClose={() => { setRequestTarget(null); setRequestReason(""); setRequestReqs(""); }}
        onSubmit={(selectedIssues) => {
          if (!requestTarget) return;
          const hasIssues = selectedIssues && selectedIssues.length > 0;
          const hasReason = requestReason.trim();
          if (!hasIssues && !hasReason) return;

          // Build reason: combine selected issue summaries with any typed reason
          const issueSummary = hasIssues
            ? selectedIssues.map((i) => `${i.detail || i.itemId.replace(/_/g, " ")}`).join("; ")
            : "";
          const combinedReason = [issueSummary, hasReason ? requestReason.trim() : ""]
            .filter(Boolean)
            .join(" — ");

          addDocumentRequest(
            requestTarget.slotId,
            requestTarget.label,
            combinedReason,
            requestReqs.split("\n").map((r) => r.trim()).filter(Boolean),
            selectedIssues
          );
          setRequestTarget(null);
          setRequestReason("");
          setRequestReqs("");
        }}
      />

      {/* Edit Existing Request Dialog */}
      <RequestRevisionDialog
        requestTarget={editingRequest ? { slotId: editingRequest.slotId, label: editingRequest.label } : null}
        requestReason={editReason}
        requestReqs={editReqs}
        validationIssues={
          editingRequest?.slotId && checklistBySlot[editingRequest.slotId]
            ? checklistBySlot[editingRequest.slotId].items.filter(
                (i) => i.status === "FAIL" || i.status === "WARNING"
              )
            : undefined
        }
        onReasonChange={setEditReason}
        onReqsChange={setEditReqs}
        onClose={() => { setEditingRequest(null); setEditReason(""); setEditReqs(""); }}
        onSubmit={async (selectedIssues) => {
          if (!editingRequest) return;
          const hasIssues = selectedIssues && selectedIssues.length > 0;
          const hasReason = editReason.trim();
          if (!hasIssues && !hasReason) return;

          const issueSummary = hasIssues
            ? selectedIssues.map((i) => `${i.detail || i.itemId.replace(/_/g, " ")}`).join("; ")
            : "";
          const combinedReason = [issueSummary, hasReason ? editReason.trim() : ""]
            .filter(Boolean)
            .join(" — ");

          await updateRequest({
            requestId: editingRequest._id,
            input: {
              reason: combinedReason,
              requirements: editReqs.split("\n").map((r) => r.trim()).filter(Boolean),
              linkedIssues: hasIssues
                ? selectedIssues.map(({ itemId, status, detail }) => ({ itemId, status, detail }))
                : [],
            },
          }).unwrap();
          setEditingRequest(null);
          setEditReason("");
          setEditReqs("");
        }}
      />

      {/* Document Request Accumulator Bar */}
      <DocumentRequestBar
        requests={pendingRequests}
        onRemove={(id) => setPendingRequests((prev) => prev.filter((r) => r.id !== id))}
        onClear={() => setPendingRequests([])}
        onSend={handleSendRequests}
        onAddCustom={(item) =>
          setPendingRequests((prev) => [
            ...prev,
            { ...item, id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}` },
          ])
        }
        sending={sendingRequests}
        existingRequestSlotIds={existingRequestSlotIds}
      />
    </Box>
  );
};
